import { useState, useEffect, useRef } from 'react';
import {
  Mail, Phone, MapPin, Instagram, Facebook, Twitter, Youtube,
  Linkedin, Music, Calendar, ExternalLink, Play, ChevronRight, ChevronLeft, Star, Quote,
  User, ArrowRight, X, Plus, Briefcase, Users, HelpCircle, MessageSquare, CheckCircle,
  Pause
} from 'lucide-react';

import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { AnimatedSection } from './AnimatedSection';

const THEMES = {
  dark_stage: {
    bg: 'bg-slate-900',
    text: 'text-white',
    textMuted: 'text-slate-400',
    card: 'bg-slate-800',
    border: 'border-slate-700',
    accent: 'text-orange-500',
    button: 'bg-orange-500 hover:bg-orange-400',
  },
  theatre_red: {
    bg: 'bg-[#1a0c0e] tx-velvet',
    text: 'text-[#f8f0e6]',
    textMuted: 'text-[#bfa39a]',
    card: 'bg-[#120709] tx-velvet-alt',
    border: 'border-[#3b2125]',
    accent: 'text-[#d4af37]',
    button: 'bg-[#b91c1c] hover:bg-[#dc2626]',
  },
  ivory_gold: {
    bg: 'bg-[#faf7f1]',
    text: 'text-[#1c1710]',
    textMuted: 'text-[#8d7f68]',
    card: 'bg-[#f1ebdd]',
    border: 'border-[#e2d8c3]',
    accent: 'text-[#b08d2f]',
    button: 'bg-[#1c1710] hover:bg-[#3a2f1e]',
  },
  light_modern: {
    bg: 'bg-white',
    text: 'text-slate-900',
    textMuted: 'text-slate-500',
    card: 'bg-slate-50',
    border: 'border-slate-200',
    accent: 'text-blue-600',
    button: 'bg-blue-600 hover:bg-blue-500',
  },
  colorful_kids: {
    bg: 'bg-amber-50',
    text: 'text-purple-900',
    textMuted: 'text-purple-600',
    card: 'bg-white',
    border: 'border-pink-200',
    accent: 'text-pink-500',
    button: 'bg-pink-500 hover:bg-pink-400',
  },
  elegant_classic: {
    bg: 'bg-stone-900',
    text: 'text-stone-50',
    textMuted: 'text-stone-400',
    card: 'bg-stone-800',
    border: 'border-stone-700',
    accent: 'text-amber-400',
    button: 'bg-amber-500 hover:bg-amber-400',
  },
  bold_artist: {
    bg: 'bg-zinc-900',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    card: 'bg-zinc-800',
    border: 'border-zinc-700',
    accent: 'text-red-500',
    button: 'bg-red-500 hover:bg-red-400',
  },
};

// Social icon mapper
const getSocialIcon = (platform) => {
  const name = platform.toLowerCase();
  if (name.includes('instagram')) return Instagram;
  if (name.includes('facebook')) return Facebook;
  if (name.includes('twitter') || name.includes('x')) return Twitter;
  if (name.includes('youtube')) return Youtube;
  if (name.includes('linkedin')) return Linkedin;
  if (name.includes('spotify') || name.includes('music')) return Music;
  return ExternalLink;
};


export const PressSection = ({ data, theme, primaryColor }) => {
  const t = THEMES[theme] || THEMES.dark_stage;
  const quotes = data?.quotes || [];
  if (quotes.length === 0) return null;

  return (
    <section id="press" className={cn("py-32 px-4 overflow-hidden relative", t.card)}>
      {/* Decorative quotes */}
      <div className="absolute top-20 left-10 opacity-5">
        <Quote className="w-40 h-40" style={{ color: primaryColor }} />
      </div>
      <div className="absolute bottom-20 right-10 opacity-5 rotate-180">
        <Quote className="w-32 h-32" style={{ color: primaryColor }} />
      </div>
      
      <div className="max-w-6xl mx-auto relative z-10">
        <AnimatedSection animation="fadeUp" className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-semibold mb-4 block" style={{ color: primaryColor }}>
            {data.subtitle || 'What The Critics Say'}
          </span>
          <h2 className={cn("text-4xl md:text-5xl font-bold", t.text)} style={{ fontFamily: "'Outfit', sans-serif" }}>
            {data.title || 'Press'}
          </h2>
        </AnimatedSection>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {quotes.map((quote, idx) => (
            <AnimatedSection
              key={quote.id || idx}
              animation="fadeUp"
              delay={idx * 150}
              className={cn(
                "relative p-8 rounded-2xl border",
                t.bg,
                t.border,
                "hover:shadow-2xl transition-shadow duration-500"
              )}
            >
              {/* Quote mark */}
              <div 
                className="absolute -top-4 left-8 w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: primaryColor }}
              >
                <Quote className="w-5 h-5 text-white" />
              </div>
              
              {/* Stars */}
              {quote.rating && (
                <div className="flex gap-1 mb-4 mt-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star 
                      key={star}
                      className={cn(
                        "w-5 h-5",
                        star <= quote.rating ? "text-amber-400 fill-amber-400" : "text-slate-600"
                      )}
                    />
                  ))}
                </div>
              )}
              
              {/* Quote text */}
              <blockquote className={cn("text-lg leading-relaxed mb-6 italic", t.text)}>
                "{quote.quote}"
              </blockquote>
              
              {/* Source */}
              <div className="mt-auto">
                {quote.publication && (
                  <p className="font-semibold" style={{ color: primaryColor }}>
                    {quote.publication}
                  </p>
                )}
                {quote.source && (
                  <p className={cn("text-sm", t.textMuted)}>
                    — {quote.source}
                  </p>
                )}
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section


